import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { io } from 'socket.io-client'

const INVITATION_PRINT_LINKS = {
  'birthday-jenine-charisse': '/debut/jenine-charisse/printable',
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const SOCKET_URL = API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:5011`

const CODE_STORAGE_KEY = 'seat_checker_access_code'

export default function ClientSeatChecker({ accessCode: propAccessCode }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [data, setData] = useState(null)
  const [selected, setSelected] = useState(null)
  const [joinedInvitationId, setJoinedInvitationId] = useState('')
  const navigate = useNavigate()

  const accessCode = useMemo(() => {
    if (propAccessCode && String(propAccessCode).trim()) return String(propAccessCode).trim().toUpperCase()
    const saved = (typeof window !== 'undefined' && localStorage.getItem(CODE_STORAGE_KEY)) || ''
    return String(saved).trim().toUpperCase()
  }, [propAccessCode])

  const endpoint = useMemo(() => {
    if (!accessCode) return null
    return `${API_BASE_URL}/rsvp/public-summary?code=${encodeURIComponent(accessCode)}`
  }, [accessCode])

  const loadSummary = useCallback(async ({ silent = false } = {}) => {
    if (!endpoint) return
    try {
      if (!silent) setLoading(true)
      setError('')
      const res = await fetch(endpoint)
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.message || 'Failed to load seat summary')
      setData(json)
      setJoinedInvitationId(json.invitationId || '')
    } catch (err) {
      setError(err.message || 'Failed to load seat summary')
      setJoinedInvitationId('')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [endpoint])

  useEffect(() => {
    if (!accessCode) {
      setLoading(false)
      setData(null)
      setJoinedInvitationId('')
      return
    }

    loadSummary()
  }, [loadSummary, accessCode])

  useEffect(() => {
    if (!accessCode) return

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] })
    socket.on('connect', () => socket.emit('seat-checker:join', { code: accessCode }))
    socket.on('seat-checker:joined', (payload) => {
      if (payload?.invitationId) setJoinedInvitationId(payload.invitationId)
    })
    socket.on('rsvp:updated', (payload) => {
      if (!payload?.invitationId || payload.invitationId === joinedInvitationId) loadSummary({ silent: true })
    })
    socket.on('seat-checker:denied', () => setError('Access code is invalid.'))

    return () => {
      if (joinedInvitationId) socket.emit('seat-checker:leave', joinedInvitationId)
      socket.disconnect()
    }
  }, [loadSummary, accessCode, joinedInvitationId])

  return (
    <section className="client-seat-checker" style={{ width: '100%' }}>
      <div style={{ width: '100%' }}>
        <h3 style={{ margin: 0, color: '#3a2c22' }}>Seat & Attendee Checker</h3>
        <p style={{ marginTop: 6, color: '#6b5a4c' }}>Invitation ID: <strong>{data?.invitationId || joinedInvitationId || '-'}</strong></p>

        {loading && <p style={{ color: '#6b5a4c' }}>Loading summary...</p>}
        {!loading && error && <p style={{ color: '#b42318' }}>{error}</p>}

        {!loading && !error && data && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginTop: 12 }}>
              <StatCard label="Total RSVPs" value={data.totalRsvps} />
              <StatCard label="Confirmed Guests" value={data.totalGuests} />
              <StatCard label="Attending Entries" value={data.attendingCount} />
              <StatCard label="Declining Entries" value={data.decliningCount} />
              <StatCard label="Seats Used" value={data.seatsUsed} />
            </div>

            <div style={{ marginTop: 18 }}>
              <h4 style={{ margin: '0 0 6px', color: '#3a2c22' }}>RSVP List</h4>
              <div style={{ marginTop: 12, overflowX: 'auto', border: '1px solid #eadfcd', borderRadius: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                  <thead>
                    <tr style={{ background: '#f4ebde', color: '#5c4b3f' }}>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>RSVP</th>
                      <th style={thStyle}>Seats</th>
                      <th style={thStyle}>Message</th>
                      <th style={thStyle}>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(data.entries) && data.entries.map((entry, idx) => (
                      <tr key={entry.id || idx} onClick={() => setSelected({ ...entry, idx })} style={{ cursor: 'pointer' }}>
                        <td style={tdStyle}>{idx + 1}</td>
                        <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name || '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontSize: 15, fontWeight: 700, color: entry.attending === 'yes' ? '#1a7a3a' : '#c0392b' }}>{entry.attending === 'yes' ? '✓' : '✕'}</td>
                        <td style={tdStyle}>{entry.seats ?? 0}</td>
                        <td style={{ ...tdStyle, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.message || '-'}</td>
                        <td style={tdStyle}>{entry.submittedAt || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {selected && <EntryModal entry={selected} onClose={() => setSelected(null)} />}
    </section>
  )
}

const thStyle = {
  textAlign: 'left', fontSize: '13px', fontWeight: 700, letterSpacing: '0.4px', padding: '10px', borderBottom: '1px solid #e1d5c2'
}

const tdStyle = {
  fontSize: '14px', padding: '10px', color: '#3a2c22', borderBottom: '1px solid #f0e7da', verticalAlign: 'top'
}

function StatCard({ label, value }) {
  return (
    <div style={{ background: '#f9f5ee', border: '1px solid #eadfcd', borderRadius: 12, padding: 12 }}>
      <p style={{ margin: 0, fontSize: 12, letterSpacing: '0.5px', color: '#7a6b5e', textTransform: 'uppercase' }}>{label}</p>
      <p style={{ margin: '8px 0 0', fontSize: 24, color: '#3a2c22', fontWeight: 700 }}>{value}</p>
    </div>
  )
}

function EntryModal({ entry, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function handleBackdrop(e) { if (e.target === e.currentTarget) onClose() }

  const isAttending = entry.attending === 'yes'

  const modal = (
    <div onClick={handleBackdrop} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: 'Georgia, serif', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#7a6b5e' }} aria-label="Close">✕</button>

        <p style={{ margin: '0 0 16px', fontSize: 11, letterSpacing: '0.5px', color: '#9a8b7e', textTransform: 'uppercase' }}>Entry #{entry.idx + 1}</p>
        <h2 style={{ margin: '0 0 4px', fontSize: 22, color: '#3a2c22' }}>{entry.name || '—'}</h2>
        <span style={{ display: 'inline-block', marginBottom: 20, padding: '3px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700, background: isAttending ? '#d4edda' : '#f8d7da', color: isAttending ? '#1a6630' : '#842029' }}>{isAttending ? '✓ Attending' : '✕ Not Attending'}</span>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <Detail label="Seats" value={entry.seats ?? 0} />
          <Detail label="Submitted" value={entry.submittedAt || '—'} />
        </div>

        {entry.message && (
          <div style={{ background: '#f9f5ee', border: '1px solid #eadfcd', borderRadius: 10, padding: 12 }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, letterSpacing: '0.5px', color: '#9a8b7e', textTransform: 'uppercase' }}>Message</p>
            <p style={{ margin: 0, fontSize: 14, color: '#3a2c22', lineHeight: 1.6 }}>{entry.message}</p>
          </div>
        )}
      </div>
    </div>
  )

  if (typeof document === 'undefined') return modal
  return createPortal(modal, document.body)
}

function Detail({ label, value }) {
  return (
    <div>
      <p style={{ margin: '0 0 2px', fontSize: 11, letterSpacing: '0.5px', color: '#9a8b7e', textTransform: 'uppercase' }}>{label}</p>
      <p style={{ margin: 0, fontSize: 15, color: '#3a2c22', fontWeight: 600 }}>{value}</p>
    </div>
  )
}

